import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Trophy, Medal, Star, Send, Loader2, Swords, FlaskConical, Gem, Skull, Shield } from "lucide-react";
import { CLASS_COLORS } from "@/lib/constants";
import { getMVP, sendMVPDiscord, getRaids } from "@/lib/api";
import { toast } from "sonner";

const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];
const MEDAL_LABELS = ["1st Place", "2nd Place", "3rd Place"];
const MEDAL_BG = ["from-yellow-500/20 to-yellow-900/5", "from-gray-400/20 to-gray-800/5", "from-orange-600/20 to-orange-900/5"];

function ScoreBar({ label, value, max, color }) {
  const pct = (value / max) * 100;
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground w-20">{label}</span>
      <div className="flex-1 h-2 bg-secondary/50 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono font-bold w-12 text-right">{value}/{max}</span>
    </div>
  );
}

function MVPCard({ mvp, rank }) {
  const classColor = CLASS_COLORS[mvp.wow_class] || CLASS_COLORS.Unknown;
  const medalColor = rank < 3 ? MEDAL_COLORS[rank] : "#64748b";
  const isTop3 = rank < 3;
  const b = mvp.breakdown;
  const s = mvp.stats;

  return (
    <div
      data-testid={`mvp-card-${rank}`}
      className={`stat-card relative overflow-hidden ${isTop3 ? `bg-gradient-to-br ${MEDAL_BG[rank]}` : ""}`}
      style={isTop3 ? { borderColor: medalColor + "40" } : {}}
    >
      {/* Medal badge */}
      <div className="absolute top-3 right-3">
        {isTop3 ? (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: medalColor + "20", border: `1px solid ${medalColor}40` }}>
            <Trophy className="h-3.5 w-3.5" style={{ color: medalColor }} />
            <span className="text-xs font-bold font-mono" style={{ color: medalColor }}>
              {MEDAL_LABELS[rank]}
            </span>
          </div>
        ) : (
          <Badge variant="outline" className="text-xs font-mono">#{rank + 1}</Badge>
        )}
      </div>

      {/* Player Info */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl font-bold font-mono"
          style={{ background: classColor + "20", color: classColor, border: `2px solid ${classColor}40` }}>
          {rank + 1}
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: classColor }}>{mvp.player_name}</h3>
          <span className="text-xs text-muted-foreground">{mvp.wow_class}</span>
        </div>
      </div>

      {/* Score */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Total Score</span>
        <span className="text-2xl font-bold font-mono" style={{ color: medalColor }}>{mvp.score}</span>
        <span className="text-sm text-muted-foreground">/100</span>
      </div>

      {/* Score breakdown bars */}
      <div className="space-y-2 mb-4">
        <ScoreBar label="Performance" value={b.perf_score ?? b.dps_score} max={40} color="#ff4444" />
        <ScoreBar label="Enchants" value={b.enchant_score} max={20} color="#1eff00" />
        <ScoreBar label="Gems" value={b.gem_score} max={10} color="#0070dd" />
        <ScoreBar label="Consumes" value={b.consume_score} max={15} color="#ff8800" />
        <ScoreBar label="Survival" value={b.survival_score} max={15} color="#a335ee" />
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-3 gap-2">
        <div className="text-center">
          <Swords className="h-3.5 w-3.5 mx-auto text-muted-foreground mb-0.5" />
          <p className="text-sm font-mono font-bold">{mvp.is_healer ? (s.hps ? s.hps.toLocaleString() : "—") : (s.dps ? s.dps.toLocaleString() : "—")}</p>
          <p className="text-[10px] text-muted-foreground">{mvp.is_healer ? "HPS" : "DPS"}</p>
        </div>
        <div className="text-center">
          <FlaskConical className="h-3.5 w-3.5 mx-auto text-muted-foreground mb-0.5" />
          <p className="text-sm font-mono font-bold">{s.consumables_used}x</p>
          <p className="text-[10px] text-muted-foreground">Consumes</p>
        </div>
        <div className="text-center">
          <Skull className="h-3.5 w-3.5 mx-auto text-muted-foreground mb-0.5" />
          <p className={`text-sm font-mono font-bold ${s.deaths > 0 ? "text-destructive" : "text-green-500"}`}>{s.deaths}</p>
          <p className="text-[10px] text-muted-foreground">Deaths</p>
        </div>
      </div>
    </div>
  );
}

export default function MVPPage() {
  const [selectedRaid, setSelectedRaid] = useState("");
  const [allRaids, setAllRaids] = useState([]);
  const [mvpData, setMvpData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [webhookOpen, setWebhookOpen] = useState(false);

  useEffect(() => {
    getRaids().then(setAllRaids).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedRaid) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getMVP(selectedRaid);
        setMvpData(res);
      } catch (e) {
        toast.error("Failed to load MVP data");
      }
      setLoading(false);
    };
    load();
  }, [selectedRaid]);

  const handleSendDiscord = async () => {
    if (!webhookUrl) { toast.error("Enter a webhook URL"); return; }
    localStorage.setItem("discord_webhook_url", webhookUrl);
    setSending(true);
    try {
      await sendMVPDiscord(webhookUrl, selectedRaid);
      toast.success("MVP celebration sent to Discord!");
      setWebhookOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="mvp-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase">
            <span className="text-yellow-500">MVP</span> of the Week
          </h1>
          <p className="text-muted-foreground mt-1">Top performers — performance, preparedness, and survival</p>
        </div>

        {mvpData && (
          <Dialog open={webhookOpen} onOpenChange={setWebhookOpen}>
            <DialogTrigger asChild>
              <Button data-testid="mvp-discord-btn" variant="outline" size="sm"
                className="border-yellow-500/40 text-yellow-500 hover:bg-yellow-500/10 gap-1.5">
                <Send className="h-3.5 w-3.5" /> Celebrate on Discord
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-background">
              <DialogHeader>
                <DialogTitle className="heading text-lg">Send MVP Celebration</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <Input value={webhookUrl}
                  onChange={(e) => { setWebhookUrl(e.target.value); localStorage.setItem("discord_webhook_url", e.target.value); }}
                  placeholder="https://discord.com/api/webhooks/..." className="bg-card/50" />
                <div className="flex justify-end gap-2">
                  <DialogClose asChild>
                    <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
                  </DialogClose>
                  <Button size="sm" disabled={!webhookUrl || sending} onClick={handleSendDiscord}
                    className="rounded-sm text-xs bg-yellow-500 text-black hover:bg-yellow-400 font-bold">
                    <Trophy className="h-3.5 w-3.5 mr-1" /> {sending ? "Sending..." : "Send Celebration"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Raid Selector */}
      <div className="mb-8 max-w-md">
        <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">Select Raid</label>
        <Select value={selectedRaid} onValueChange={setSelectedRaid}>
          <SelectTrigger data-testid="mvp-raid-selector" className="bg-card/50">
            <SelectValue placeholder="Choose a raid..." />
          </SelectTrigger>
          <SelectContent>
            {allRaids.map((r) => (
              <SelectItem key={r.id} value={r.id}>{r.raid_name} — {r.date}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedRaid && (
        <Card className="bg-card/50 border-border">
          <CardContent className="py-12 text-center">
            <Trophy className="h-10 w-10 text-yellow-500/50 mx-auto mb-3" />
            <p className="text-muted-foreground">Select a raid to see the top performers</p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="flex items-center justify-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-yellow-500" />
        </div>
      )}

      {mvpData && !loading && (
        <>
          {/* Top 3 Podium */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8" data-testid="mvp-podium">
            {mvpData.top3.map((mvp, i) => (
              <MVPCard key={mvp.player_id} mvp={mvp} rank={i} />
            ))}
            {mvpData.top3.length === 0 && (
              <Card className="bg-card/50 border-border col-span-3">
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">No player data for this raid yet.</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Full Rankings Table */}
          {mvpData.rankings.length > 3 && (
            <div>
              <h2 className="heading text-xl font-semibold uppercase tracking-tight mb-3">Full Rankings</h2>
              <div className="space-y-1">
                <div className="grid grid-cols-[40px_1fr_80px_80px_60px_60px_60px_60px] gap-2 px-3 py-1.5">
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">#</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">Player</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">Score</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">DPS/HPS</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">Ench</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">Gems</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">Cons</span>
                  <span className="text-[10px] font-mono uppercase text-muted-foreground">Deaths</span>
                </div>
                {mvpData.rankings.slice(3).map((p, i) => {
                  const classColor = CLASS_COLORS[p.wow_class] || CLASS_COLORS.Unknown;
                  return (
                    <div key={p.player_id} data-testid={`ranking-row-${i + 3}`}
                      className="grid grid-cols-[40px_1fr_80px_80px_60px_60px_60px_60px] gap-2 px-3 py-2 items-center hover:bg-card/50 border-b border-border/20">
                      <span className="text-sm font-mono text-muted-foreground">#{i + 4}</span>
                      <span className="text-sm font-medium truncate" style={{ color: classColor }}>{p.player_name}</span>
                      <span className="text-sm font-mono font-bold">{p.score}</span>
                      <span className="text-sm font-mono">{p.is_healer ? (p.stats.hps ? p.stats.hps.toLocaleString() : "—") : (p.stats.dps ? p.stats.dps.toLocaleString() : "—")}</span>
                      <span className="text-sm font-mono">{p.stats.enchants}</span>
                      <span className="text-sm font-mono">{p.stats.gems}</span>
                      <span className="text-sm font-mono">{p.stats.consumables_used}</span>
                      <span className={`text-sm font-mono ${p.stats.deaths > 0 ? "text-destructive" : ""}`}>{p.stats.deaths}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Score Formula */}
          <div className="mt-6 stat-card p-4">
            <h3 className="heading text-base font-semibold uppercase tracking-tight mb-2">Score Formula</h3>
            <p className="text-xs text-muted-foreground">
              Performance (40pts) + Enchant Compliance (20pts) + Gem Compliance (10pts) + Consumable Usage (15pts) + Survival (15pts) = 100pts max. Healers are ranked by HPS vs healer peers, DPS by DPS vs DPS peers.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
